import { messageService } from "@/services/messageService/clientMessageService";
import type { TopicDataMap } from "@repo/types";
import { wrapAsyncLast } from "@repo/lib/wrapAsync";
import type { Observable } from "rxjs";

export interface Subscription {
  unsubscribe(): void;
}

export const cacheDefaults = {
  // 300 seconds = 5 minutes
  expiresAfterSeconds: 300,
  purgeExpiredAfterSeconds: 300,
};

export const hookPriorities = {
  // before
  autoPublish: 5,
  switchItemTeam: 5,
  removeNestedEntities: 100,
  // after
  postSaveActions: 10,
  // both
  detectOutdatedSigningTemplate: -1,
};

/** Subscribe to a channel on the messaging service. Automatically waits for previous handler
 * calls to resolve before re-running, when many messages are received for the same entity. */
export const messageServiceSubscribe = (
  topic: keyof TopicDataMap,
  keys: string[],
  handler: () => Promise<void>
): Subscription => {
  handler = wrapAsyncLast(handler);
  return messageService.getInstance().subscribe(topic, keys, handler);
};

/** Determine difference in nested subscriptions: Subscribe to any suddenly present children,
 * unsubscribe from any suddenly missing children, and update the subscription table
 * accordingly. */
export const diffChildSubscriptions = <Child>({
  subscriptionTable,
  idArray,
  objectArray,
  subscribeChild,
  publishParent,
}: {
  subscriptionTable: Record<string, Subscription>;
  idArray: string[];
  objectArray: Child[];
  subscribeChild: (id: string) => Observable<Child | null>;
  publishParent: (idArray: string[], objectArray: Child[]) => void;
}) => {
  const newTable: Record<string, Subscription> = Object.create(null);

  idArray = idArray.slice();
  objectArray = objectArray.slice();

  for (const childId of idArray) {
    if (subscriptionTable[childId]) {
      newTable[childId] = subscriptionTable[childId];
      delete subscriptionTable[childId];
    } else if (!newTable[childId]) {
      newTable[childId] = subscribeChild(childId).subscribe((child) => {
        let found = false;

        // find and handle all occurrences of the child by id
        for (let i = 0; i < idArray.length; i++) {
          if (idArray[i] === childId) {
            found = true;

            if (!child) {
              idArray.splice(i, 1);
              objectArray.splice(i, 1);
              i--;
            } else {
              objectArray[i] = child;
            }
          }
        }

        if (found) publishParent(idArray.slice(), objectArray.slice());
      });
    }
  }
  for (const childId in subscriptionTable) {
    subscriptionTable[childId].unsubscribe();
    delete subscriptionTable[childId];
  }
  Object.assign(subscriptionTable, newTable);
};

export interface SubscribeEntry {
  subscription?: Subscription;
  childSubscriptions: Record<string, Subscription>;
}

/** A table for storing and managing a repository's entity and child subscriptions. */
export class SubscriptionMap {
  table = new Map<string, SubscribeEntry>();

  /** Add a new subscription for an entity. Prepares a table for its child subscriptions. */
  addSubscription(strKey: string, subscription?: Subscription) {
    this.table.set(strKey, {
      subscription,
      childSubscriptions: Object.create(null),
    });
  }

  /** Updates the set of child subscriptions, by diffing between current child subscriptions and
   * the entity's new set of children. */
  updateChildren<Child>({
    strKey,
    idArray,
    objectArray,
    subscribeChild,
    publishParent,
  }: {
    strKey: string;
    idArray: string[];
    objectArray: Child[];
    subscribeChild: (id: string) => Observable<Child | null>;
    publishParent: (idArray: string[], objectArray: Child[]) => void;
  }) {
    const entry = this.table.get(strKey);
    if (!entry) return;

    diffChildSubscriptions({
      subscriptionTable: entry.childSubscriptions,
      idArray,
      objectArray,
      subscribeChild,
      publishParent,
    });
  }

  /** Unsubscribes all subscriptions for this entity, both direct and child subscriptions. */
  unsubscribe(strKey: string) {
    const entry = this.table.get(strKey);
    if (entry) {
      this.table.delete(strKey);

      entry.subscription?.unsubscribe();
      for (const childId in entry.childSubscriptions) {
        entry.childSubscriptions[childId].unsubscribe();
      }
    }
  }
}
